import { DecimalPipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ICanDeactivateGuard } from '../../../core/CanDeactivateGuard';
import { IClassifier } from '../../../models/Classifier';
import { IPersonSearchResultItem } from '../../../models/Person';
import {
    IStudentCourseEqualization, IStudentCourseEqualizationCourse, IStudentCourseEqualizationEditModel, IStudentCourseEqualizationFile,
    IStudentCourseEqualizationInner, IStudentCourseEqualizationInnerEditModel, IStudentCourseEqualizationOuter,
    IStudentCourseEqualizationStudent
} from '../../../models/StudentCourseEqualization';
import { AppService } from '../../../services/app.service';
import { ClassifierService } from '../../../services/classifier.service';
import { ParameterService } from '../../../services/parameter.service';
import { StudentCourseEqualizationService } from '../../../services/student-course-equalization.service';
import { createFileStub } from '../../../shared/file/file.component';
import { ITableColumn } from '../../../shared/table/table.component';
import { StudentCourseEqualizationApprovalComponent } from '../approval/student-course-equalization-approval.component';
import { IInnerCourseOptions, StudentCourseEqualizationInnerComponent } from '../inner/student-course-equalization-inner.component';
import { IOuterCourseOptions, StudentCourseEqualizationOuterComponent } from '../outer/student-course-equalization-outer.component';
import { StudentCourseEqualizationReturnComponent } from '../return/student-course-equalization-return.component';
import { permissions, store, util } from '../shared';

@Component({
    selector: 'app-student-course-equalization-form',
    templateUrl: './student-course-equalization-form.component.html',
    styleUrls: ['../student-course-equalization.component.scss']
})
export class StudentCourseEqualizationFormComponent implements OnInit, ICanDeactivateGuard {
    constructor(
        private app: AppService,
        private service: StudentCourseEqualizationService,
        private classifiers: ClassifierService,
        private parameters: ParameterService,
        private modal: NgbModal,
        private route: ActivatedRoute,
        private numberPipe: DecimalPipe
    ) { }

    courseOptions: IStudentCourseEqualizationCourse[] = [];
    gradeOptions: IClassifier[] = [];
    courseTypeOptions: IClassifier[] = [];
    enrollSemesterOptions: { text: string, value: number }[] = [];
    /**
     * Available specialities per student
     */
    studentOptions: IStudentCourseEqualizationStudent[] = [];

    /**
     * Selected student/speciality
     */
    student: IStudentCourseEqualizationStudent;

    model: IStudentCourseEqualization = <IStudentCourseEqualization>{
        Attachments: [],
        Courses: []
    };

    attachmentExtensions: string = '';
    attachmentMaxSize: number = 0;
    attachmentLimit: number = 0;

    canPickPerson: boolean;

    formSubmitted: boolean;
    personPickerOpened: boolean;

    isUnavailable: boolean;
    isNew: boolean;
    isReady: boolean;
    isCourseDataReady: boolean;

    readonly util = util;
    readonly specialityDisplayFn = (option: IStudentCourseEqualizationStudent) => `${option.SpecialityCode} - ${option.SpecialityName}`;

    readonly outerColumns: ITableColumn[] = [
        { label: 'studentCourseEqualization_lblCourse' },
        { label: 'studentCourseEqualization_lblEcts', cssClass: 'hidden-sm-down' },
        { label: 'studentCourseEqualization_lblGrade', cssClass: 'hidden-sm-down' },
        { label: 'studentCourseEqualization_lblAttachments', cssClass: 'hidden-md-down' },
        { width: '1px' }
    ];

    readonly seminarColumns: ITableColumn[] = [
        { width: '1px', label: 'studentCourseEqualization_lblEqualize' },
        { label: 'studentCourseEqualization_lblSeminarName' },
        { label: 'studentCourseEqualization_lblLecturer' },
        { label: 'studentCourseEqualization_lblLocation' }
    ];

    get isPowerUser() {
        return this.canCreateProject;
    }

    get canEdit() {
        return this.courseOptions.length > 0
            && (this.canCreateProject || (this.editAllowed && (this.model?.Status == 'Draft' || this.model?.Status == 'ReturnedForAddition')));
    }

    get canSubmit() {
        return this.editAllowed && (this.model?.Status == 'Draft' || this.model?.Status == 'ReturnedForAddition') && this.courseOptions.length > 0;
    }

    get canCreateProject() {
        return this.projectAllowed && this.model?.Status == 'Submitted';
    }

    get selectedCourseCount() {
        return this.model.Courses.length;
    }

    get totalAttachments() {
        return this.model.Attachments.filter(t => !!t.FileName).length
            + this.model.Courses.reduce((sum, n) => {
                sum += n.Courses.reduce((s, k) => {
                    s += k.Attachments.filter(a => !!a.FileName).length;
                    return s;
                }, 0);

                return sum;
            }, 0);
    }

    get hasIncompleteCourse() {
        return this.model.Courses.some(t => !this.checkCourseValidity(t));
    }

    get dvsProtocolUrl(): string {
        return this.model?.Status == 'Registered' ? this.service.getDvsProtocolUrl(this.model.Id) : undefined;
    }

    private originalData: string;
    private originalAttachments: {
        id: number,
        name: string,
        innerCourse?: string,
        innerCourseId?: number,
        outerCourse?: string,
        outerCourseId?: number
    }[] = [];
    private isSaved: boolean;
    private decisionOptions: IClassifier[] = [];
    private courseDecisionOptions: IClassifier[] = [];
    private editAllowed: boolean;
    private projectAllowed: boolean;

    private readonly studentCache: { [email: string]: IStudentCourseEqualizationStudent[] } = {};
    private readonly courseCache: { [specialityId: string]: IStudentCourseEqualizationCourse[] } = {};

    @ViewChild('form', { static: false }) private form: NgForm;

    ngOnInit() {
        const user = this.app.currentUser;
        const id = this.route.snapshot.params['id'];

        this.editAllowed = user.rights.indexOf(permissions.edit) > -1;
        this.projectAllowed = user.rights.indexOf(permissions.createProject) > -1;

        if (!id) {
            this.isNew = true;
            this.model.Status = 'Draft';

            this.canPickPerson = user.rights.indexOf(permissions.setStudent) > -1;

            if (this.canPickPerson) {
                const person = store.getStudent();

                if (person) {
                    this.initForm('edit', person.Email);
                } else {
                    this.togglePersonPicker();
                }
            } else {
                this.initForm('edit');
            }
        } else {
            this.loadById(+id);
        }
    }

    canDeactivate(): Observable<boolean> {
        if (!this.personPickerOpened && !this.isSaved && this.hasChanges()) {
            const subj = new Subject<boolean>();
            const text = this.isNew ? 'studentCourseEqualization_confirmUnsavedNew' : 'studentCourseEqualization_confirmUnsavedEdit';

            this.app.confirm(this.app.translate(text), result => {
                subj.next(result);
            });

            return subj.asObservable();
        }

        return of(true);
    }

    setSpeciality(student: IStudentCourseEqualizationStudent) {
        this.student = student;

        this.model.SpecialityId = student.SpecialityId;
        this.model.StudentEmail = student.Email;
        this.model.StudentEmail = student.Email;
        this.model.SpecialityId = student.SpecialityId;

        this.enrollSemesterOptions = [];

        if (student.SpecialityYears) {
            let year = 0;

            for (let i = 1; i <= student.SpecialityYears * 2; i++) {
                year += (i % 2);

                this.enrollSemesterOptions.push({
                    text: this.app.translate('studentCourseEqualization_enrollSemester')
                        .replace('{{semester}}', i)
                        .replace('{{year}}', year),
                    value: i
                });
            }
        }

        if (!store.getStudent()) {
            store.setStudent(<IPersonSearchResultItem>{
                Email: student.Email,
                FirstName: student.Name,
                LastName: student.Surname
            });
        }

        this.app.addLoading(this.loadCourses(this.model.SpecialityId, this.model.StudentEmail)).subscribe(data => {
            this.isCourseDataReady = true;
            this.courseOptions = data;
        });
    }

    togglePersonPicker() {
        this.personPickerOpened = !this.personPickerOpened;

        if (this.personPickerOpened) {
            this.model.StudentEmail = undefined;
            this.model.SpecialityId = undefined;

            this.student = undefined;
            this.studentOptions = [];
            this.isUnavailable = false;
        }
    }

    pickPerson(person: IPersonSearchResultItem) {
        this.personPickerOpened = false;
        this.isUnavailable = false;

        this.initForm('edit', person.Email);
    }

    addCourse(inner?: IStudentCourseEqualizationInner) {
        let outer = <IStudentCourseEqualizationOuter>{
            Attachments: []
        };

        const ref = this.modal.open(StudentCourseEqualizationOuterComponent, { size: 'lg' });

        ref.componentInstance.data = outer;
        ref.componentInstance.options = <IOuterCourseOptions>{
            attachmentExtensions: this.attachmentExtensions,
            attachmentMaxSize: this.attachmentMaxSize,
            innerCourses: this.courseOptions,
            courseTypes: this.courseTypeOptions,
            grades: this.gradeOptions,
            //canOnlyAppendFiles: this.canOnlyAppendFiles,
            canEdit: this.canEdit
        };

        if (inner) {
            ref.componentInstance.innerCourse = this.courseOptions.find(t => t.PartId == inner.PartId);
        }

        ref.result.then((result: { innerCourse: IStudentCourseEqualizationCourse, outerCourse: IStudentCourseEqualizationOuter }) => {
            const innerAfter = result.innerCourse;
            const outerAfter = result.outerCourse;

            outerAfter.IsMy = true;

            let inner = this.model.Courses.find(t => t.PartId == innerAfter.PartId);

            if (!inner) {
                const course = this.courseOptions.find(t => t.PartId == innerAfter.PartId);
                inner = {
                    AcquireEcts: undefined,
                    Code: course.Code,
                    PartId: course.PartId,
                    Ects: course.Ects,
                    Id: undefined,
                    Name: course.Name,
                    StudyYear: course.StudyYear,
                    Courses: [],
                    IsMy: true
                };
                this.model.Courses.push(inner);
            }

            inner.Courses.push(outerAfter);
        }, () => { });
    }

    viewCourse(inner: IStudentCourseEqualizationInner, outer: IStudentCourseEqualizationOuter) {
        const ref = this.modal.open(StudentCourseEqualizationOuterComponent);

        ref.componentInstance.options = <IOuterCourseOptions>{
            innerCourses: this.courseOptions,
            courseTypes: this.courseTypeOptions,
            grades: this.gradeOptions
        };
        ref.componentInstance.innerCourse = this.courseOptions.find(t => t.PartId == inner.PartId);
        ref.componentInstance.data = outer;
    }

    editCourse(inner: IStudentCourseEqualizationInner, outer?: IStudentCourseEqualizationOuter) {
        if (outer) {
            const ref = this.modal.open(StudentCourseEqualizationOuterComponent, { size: 'lg' });

            ref.componentInstance.options = <IOuterCourseOptions>{
                attachmentExtensions: this.attachmentExtensions,
                attachmentMaxSize: this.attachmentMaxSize,
                innerCourses: this.courseOptions,
                courseTypes: this.courseTypeOptions,
                grades: this.gradeOptions,
                canEdit: this.canEdit
            };
            ref.componentInstance.data = outer;
            ref.componentInstance.innerCourse = this.courseOptions.find(t => t.PartId == inner.PartId);

            ref.result.then((result: { innerCourse: IStudentCourseEqualizationCourse, outerCourse: IStudentCourseEqualizationOuter }) => {
                const innerAfter = result.innerCourse;
                const outerAfter = result.outerCourse;

                // update values
                for (const k in outerAfter) {
                    outer[k] = outerAfter[k];
                }

                let exInner = this.model.Courses.find(t => t.PartId == innerAfter.PartId);

                if (!exInner) {
                    const course = this.courseOptions.find(t => t.PartId == innerAfter.PartId);

                    exInner = {
                        AcquireEcts: undefined,
                        Code: course.Code,
                        PartId: course.PartId,
                        Ects: course.Ects,
                        Id: undefined,
                        Name: course.Name,
                        StudyYear: course.StudyYear,
                        Courses: [outer],
                        IsMy: true
                    };

                    this.model.Courses.push(exInner);
                } else if (innerAfter.PartId != inner.PartId) {
                    // move to selected inner course
                    exInner.Courses.push(outer);
                    this.removeCourse(inner, outer, false);
                }
            }, () => { });
        } else {
            const opts = <IInnerCourseOptions>{
                grades: this.gradeOptions,
                decisions: this.courseDecisionOptions
            };

            if (inner.Courses.length == 1) {
                opts.defaultGradeId = inner.Courses[0].GradeId;
            }

            const ref = this.modal.open(StudentCourseEqualizationInnerComponent, { size: 'lg' });

            ref.componentInstance.data = { course: inner, options: opts };

            ref.result.then(({ model }) => {
                for (const k in model) {
                    inner[k] = model[k];
                }
            }, () => { });
        }
    }

    removeCourse(inner: IStudentCourseEqualizationInner, outer?: IStudentCourseEqualizationOuter, confirm: boolean = true) {
        const proceed = () => {
            if (outer) {
                const ix = inner.Courses.indexOf(outer);
                inner.Courses.splice(ix, 1);
            } else {
                const ix = this.model.Courses.indexOf(inner);
                this.model.Courses.splice(ix, 1);
            }
        };

        if (confirm) {
            this.app.confirm({
                text: this.app.translate('studentCourseEqualization_confirmCourseRemove').replace('{{course}}', outer ? outer.Name : inner.Name),
                title: this.app.translate('studentCourseEqualization_removeCourse')
            }, ok => {
                if (ok) {
                    proceed();
                }
            });
        } else {
            proceed();
        }
    }

    save() {
        this.formSubmitted = true;
        if (!this.validate()) return;

        this.app.addLoading(this.doSave()).subscribe(complete => {
            if (complete) {
                this.app.notify(this.app.translate('studentCourseEqualization_saved'));
                this.app.navigateByUrl('/student-course-equalizations');
            } else {
                this.reload();
            }
        });
    }

    submit() {
        this.formSubmitted = true;
        if (!this.validate()) return;

        this.app.addLoading(this.doSave()).subscribe(complete => {
            if (complete) {
                this.app.addLoading(this.service.submit(this.model.Id)).subscribe(() => {
                    this.app.notify(this.app.translate('studentCourseEqualization_submitted'));
                    this.app.navigateByUrl('/student-course-equalizations');
                }, err => {
                    this.app.showError(err);
                    this.reload();
                });
            } else {
                this.reload();
            }
        });
    }

    returnForAddition() {
        const ref = this.modal.open(StudentCourseEqualizationReturnComponent);

        ref.result.then((result: { comment: string }) => {
            if (result) {
                this.app.addLoading(this.doSave()).subscribe(complete => {
                    if (complete) {
                        this.app.addLoading(this.service.returnForAddition(this.model.Id, result.comment)).subscribe(() => {
                            this.app.notify(this.app.translate('studentCourseEqualization_returnedForAddition'));
                            this.app.navigateByUrl('/student-course-equalizations');
                        }, err => {
                            this.app.showError(err);
                            this.reload();
                        });
                    } else {
                        this.reload();
                    }
                });
            }
        }, () => { });
    }

    createProject() {
        this.formSubmitted = true;
        if (!this.validate()) return;

        const overallDecisionMap = {
            full: this.decisionOptions.find(t => t.Code == 'PassFull'),
            partial: this.decisionOptions.find(t => t.Code == 'PassPartial'),
            fail: this.decisionOptions.find(t => t.Code == 'Fail'),
            other: this.decisionOptions.find(t => t.Code == 'Other')
        };

        const decisions: {
            decision: IClassifier,
            details: {
                course: string,
                ects?: string,
                comments?: string
            }[]
        }[] = [
                { decision: this.courseDecisionOptions.find(t => t.Code == 'PassFull'), details: [] },
                { decision: this.courseDecisionOptions.find(t => t.Code == 'PassPartial'), details: [] },
                { decision: this.courseDecisionOptions.find(t => t.Code == 'Fail'), details: [] }
            ];

        const totalCourses = this.model.Courses.length;

        let fullPass = 0;
        let partialPass = 0;
        let noPass = 0;

        const decisionLabel = this.app.translate('studentCourseEqualization_lblDecision');
        const outOfLabel = this.app.translate('studentCourseEqualization_outOf');
        const ectsDisplay = (n: number) => {
            return this.numberPipe.transform(n, '1.0-2', 'en-US');
        };

        this.model.Courses.forEach(t => {
            const decision = this.courseDecisionOptions.find(n => n.Id == t.ApprovalDecisionId);

            let group = decisions.find(n => n.decision.Id == decision.Id);

            if (!group) {
                group = { decision, details: [] };
                decisions.push(group);
            }

            if (decision.Code == 'Fail') noPass++;
            else if (decision.Code == 'PassFull') fullPass++;
            else if (decision.Code == 'PassPartial') partialPass++;

            const ectsDetails = decision.Code == 'PassPartial'
                ? `${ectsDisplay(t.AcquireEcts)} ${outOfLabel} ${ectsDisplay(t.Ects)} ECTS`
                : `${t.Ects} ECTS`;

            group.details.push({
                course: `${t.Code} ${t.Name}`,
                ects: ectsDetails,
                comments: t.ApprovalComment
            });
        });

        const decision = (fullPass == totalCourses ? overallDecisionMap.full
            : (fullPass || partialPass) ? overallDecisionMap.partial
                : noPass == totalCourses ? overallDecisionMap.fail
                    : undefined) || overallDecisionMap.other;

        if (!decision) throw new Error('Unable to determine the final decision based on the current classifier data.');

        const ref = this.modal.open(StudentCourseEqualizationApprovalComponent);

        const validDecisions = decisions.filter(t => !!t.decision && t.details.length);
        const comment = validDecisions.map(t => {
            return `${decisionLabel}: ${t.decision.Value}\n`
                + t.details.map(d => `${d.course}${d.ects ? ', ' + d.ects : ''}${d.comments ? ', ' + d.comments : ''}`).join('\n');
        }).join('\n\n');

        ref.componentInstance.data = {
            id: this.model.Id,
            decisions: validDecisions,
            comment
        };

        ref.result.then((result: boolean) => {
            if (result) {
                this.app.addLoading(this.doSave()).subscribe(complete => {
                    if (complete) {
                        this.app.addLoading(this.service.createProject(this.model.Id, decision.Id, comment)).subscribe(() => {
                            this.app.notify(this.app.translate('studentCourseEqualization_projectCreated'));
                            this.app.navigateByUrl('/student-course-equalizations');
                        }, err => {
                            this.app.showError(err);
                            this.reload();
                        });
                    } else {
                        this.reload();
                    }
                });
            }
        }, () => { });
    }

    addAttachment() {
        this.model.Attachments.push(<IStudentCourseEqualizationFile>{});
    }

    removeAttachment(file: IStudentCourseEqualizationFile) {
        const i = this.model.Attachments.indexOf(file);

        if (i > -1) {
            this.model.Attachments.splice(i, 1);
        }
    }

    onFileChange(event: File, file: IStudentCourseEqualizationFile) {
        if (!event) {
            this.removeAttachment(file);
        } else {
            file.Id = null;
            file.FileName = event.name;
        }
    }

    checkDuplicateFileName(file: IStudentCourseEqualizationFile): boolean {
        if (!file.File) return false;
        return this.model.Attachments.filter(t => t.FileName == file.File.name).length > 1;
    }

    downloadAttachment(attachment: IStudentCourseEqualizationFile) {
        util.downloadAttachment(this.app, this.service, attachment);
    }

    getCreditedEcts(course: IStudentCourseEqualizationInner): number {
        return course.AcquireEcts == undefined ? 0 : course.Ects - course.AcquireEcts;
    }

    checkCourseValidity(course: IStudentCourseEqualizationInner): string {
        return course.ApprovalDecisionCode;
    }

    private validate() {
        if (!this.form.valid) {

            this.app.alert.error(this.app.translate('studentCourseEqualization_formError'));
            return false;
        }

        if (this.totalAttachments > this.attachmentLimit) {
            this.app.alert.error(this.app.translate('studentCourseEqualization_maxFileCountExceededDetailed').replace('{0}', this.attachmentLimit));
            return false;
        }

        return true;
    }

    private loadById(id: number) {
        this.isSaved = false;

        this.app.addLoading(this.service.getById(id)).subscribe(data => {
            this.model = data;

            data.Attachments.forEach(t => {
                t.File = createFileStub(t.FileName);
            });

            this.student = {
                Email: data.StudentEmail,
                Name: data.PersonName,
                PersonCode: data.PersonCode,
                SpecialityCode: data.SpecialityCode,
                SpecialityId: data.SpecialityId,
                SpecialityName: data.SpecialityName,
                Status: '...', // status is loaded later
                StudyYear: data.StudyYear,
                Surname: data.PersonSurname,
                ContactPersonFirstName: data.ContactPersonFirstName,
                ContactPersonLastName: data.ContactPersonLastName,
                SupervisorFirstName: data.SupervisorFirstName,
                SupervisorLastName: data.SupervisorLastName
            };

            this.courseOptions = data.Courses.map(t => {
                return {
                    Code: t.Code,
                    PartId: t.PartId,
                    Ects: t.Ects,
                    Name: t.Name,
                    StudyYear: t.StudyYear
                };
            });

            this.originalData = JSON.stringify(data);

            this.originalAttachments = data.Attachments.map(t => {
                return {
                    id: t.Id,
                    name: t.FileName
                };
            });

            data.Courses.forEach(c => {
                c.Courses.forEach(n => {
                    const outerFiles = n.Attachments.map(t => {
                        return {
                            id: t.Id,
                            name: t.FileName,
                            innerCourse: `${c.Code} ${c.Name}`,
                            innerCourseId: c.Id,
                            outerCourse: n.Name,
                            outerCourseId: n.Id
                        };
                    });
                    this.originalAttachments.push(...outerFiles);
                });
            });

            this.initForm(this.canEdit ? 'edit'/* : this.canOnlyAppendFiles ? 'filesOnly'*/ : 'read', data.StudentEmail, data.SpecialityId);
        });
    }

    private loadStudent(email?: string) {
        const key = email || '.';
        const cached = this.studentCache[key];

        if (cached) {
            this.studentOptions = cached;
            return of(cached);
        }

        return this.app.addLoading(this.service.getStudent(email)).pipe(tap(data => {
            this.studentCache[key] = data;
        }));
    }

    private loadCourses(specialityId: string, email?: string) {
        const cached = this.courseCache[specialityId];

        if (cached) {
            this.courseOptions = cached;
            return of(cached);
        }

        return this.app.addLoading(this.service.getCourses(specialityId, email)).pipe(tap(data => {
            this.courseCache[specialityId] = data;
        }));
    }

    private loadConfig() {
        const classifierCodes = [
            'StudentCourseEqualizationStudyCourseType',
            'StudentCourseEqualizationDecision',
            'StudentCourseEqualizationCourseDecision',
            'Grade'
        ];

        const reqs = forkJoin(
            this.parameters.getValues(),
            this.classifiers.get(classifierCodes.join(','))
        );

        return reqs.pipe(tap(([params, classifiers]) => {
            this.attachmentMaxSize = this.parameters.findValue(params, 'StudentCourseEqualizationFileMaxSize', t => +t);
            this.attachmentLimit = this.parameters.findValue(params, 'StudentCourseEqualizationFileLimit', t => +t);
            this.attachmentExtensions = this.parameters.findValue(params, 'StudentCourseEqualizationFileExtensions', t => '.' + t.replace(',', ',.'));

            this.courseTypeOptions = classifiers.filter(t => t.Type == 'StudentCourseEqualizationStudyCourseType');
            this.decisionOptions = classifiers.filter(t => t.Type == 'StudentCourseEqualizationDecision');
            this.courseDecisionOptions = classifiers.filter(t => t.Type == 'StudentCourseEqualizationCourseDecision');

            const getDecisionOrder = (n: IClassifier) => {
                switch (n.Code) {
                    case 'PassFull': return 1;
                    case 'PassPartial': return 2;
                    case 'Fail': return 3;
                    case 'Other': return 100;
                    default: return 4;
                }
            };

            this.decisionOptions.sort((a, b) => getDecisionOrder(a) - getDecisionOrder(b));
            this.courseDecisionOptions.sort((a, b) => getDecisionOrder(a) - getDecisionOrder(b));

            const grades = classifiers.filter(t => t.Type == 'Grade');

            grades.sort((a, b) => {
                return this.getGradeSortingNumber(b) - this.getGradeSortingNumber(a);
            });

            this.gradeOptions = grades;
        }));
    }

    private initForm(mode: 'read' | 'edit'/* | 'filesOnly'*/, email?: string, specialityId?: string) {
        let needConfig = false;

        this.loadStudent(email).subscribe(student => {
            switch (mode) {
                case 'edit':
                    //case 'filesOnly':
                    this.studentOptions = student;

                    if (student.length == 0) {
                        this.isUnavailable = true;
                    } else {
                        let spec: IStudentCourseEqualizationStudent;

                        if (specialityId) {
                            spec = student.find(t => t.SpecialityId == specialityId);
                        } else if (student.length == 1) {
                            spec = student[0];
                        }

                        if (spec) {
                            this.setSpeciality(spec);
                            // always show the fresh status for the spected speciality
                            this.student.Status = spec.Status;
                        }
                    }

                    needConfig = true;

                    break;

                default:
                    this.isCourseDataReady = true;
                    break;
            }

            if (needConfig) {
                this.loadConfig().subscribe(() => {
                    this.isReady = true;
                });
            } else {
                this.isReady = true;
            }
        });
    }

    /**
     * Save form values to the database.
     * @returns A subject indicating save status:
     * true means that everything was successfully saved, false means that an error occured while processing files.
     */
    private doSave() {
        let id: number;

        const failedToAdd = this.app.translate('studentCourseEqualization_fileAddFailed');
        const failedToRemove = this.app.translate('studentCourseEqualization_fileRemoveFailed');

        const fileErrors: {
            innerCourse?: string,
            outerCourse?: string,
            name: string,
            error: string
        }[] = [];

        const showFileErrors = () => {
            const message = fileErrors.map((t, i) => {
                const course = t.outerCourse ? `${t.outerCourse} / ${t.innerCourse}` : undefined;
                return `<div class="mt-${i > 0 ? 1 : 0}">`
                    + (course ? `<div class="text-truncate" title="${course}"><small>${course}</small></div>` : '')
                    + `<div><strong>${t.name}</strong></div>`
                    + `<div class="text-danger">${t.error}</div></div>`;
            }).join('');

            this.app.showError(message);
        };

        const subj = new Subject<boolean>();

        const model: IStudentCourseEqualizationEditModel = {
            Courses: this.model.Courses.map(t => {
                return <IStudentCourseEqualizationInnerEditModel>{
                    Id: t.Id,
                    StudyYear: t.StudyYear,
                    Code: t.Code,
                    Courses: t.Courses.map(n => {
                        return {
                            Id: n.Id,
                            Ects: n.Ects,
                            Name: n.Name,
                            Notes: n.Notes,
                            Programme: n.Programme,
                            GradeId: n.GradeId,
                            TypeId: n.TypeId,
                            University: n.University
                        };
                    }),
                    AcquireEcts: t.AcquireEcts,
                    GradeId: t.GradeId,
                    IsEqualizedWithNoGrade: t.IsEqualizedWithNoGrade,
                    ApprovalComment: t.ApprovalComment,
                    ApprovalDecisionId: t.ApprovalDecisionId
                };
            }),
            SpecialityId: this.model.SpecialityId,
            StudentEmail: this.model.StudentEmail,
            EnrollSemester: this.model.EnrollSemester
        };

        const primary = this.isNew ? this.service.create(model) : this.service.update(this.model.Id, model);

        primary.subscribe(result => {
            this.model.Id = id = result.Id;

            result.Courses.forEach((t, i) => {
                t.Courses.forEach((n, j) => {
                    const courseModel = this.model.Courses[i].Courses[j];

                    if (!courseModel) {
                        // returned courses must be the same user submitted
                        throw new Error(this.app.translate('studentCourseEqualization_saveError'));
                    }

                    courseModel.Id = n.Id;
                });
            });

            const filesToRemove = this.originalAttachments.filter(oa => {
                let rem = true;

                if (this.model.Attachments.find(ma => ma.Id == oa.id)) {
                    // file still exists as main form attachment
                    rem = false;
                } else {
                    const ownerCourse = result.Courses.some(ic => ic.Courses.some(oc => oc.Id == oa.outerCourseId));

                    // ensure owner course still exists, as it might be already deleted with all included attachments by the primary save
                    if (ownerCourse) {
                        this.model.Courses.forEach(ic => {
                            ic.Courses.forEach(oc => {
                                if (oc.Attachments.find(a => a.Id == oa.id)) {
                                    // file still exists as outer course attachment
                                    rem = false;
                                }
                            });
                        });
                    } else {
                        rem = false;
                    }
                }

                return rem;
            }).map(t => {
                return this.app.addLoading(this.service.removeAttachment(t.id)).pipe(tap(() => {
                    const ix = this.originalAttachments.indexOf(t);
                    this.originalAttachments.splice(ix, 1);
                }), catchError(err => {
                    fileErrors.push({
                        name: t.name,
                        innerCourse: t.innerCourse,
                        outerCourse: t.outerCourse,
                        error: failedToRemove + ' ' + this.app.getHttpResponseError(err)
                    });
                    return of(0);
                }));
            });

            const filesToAdd = this.model.Attachments.filter(t => !!t.File?.size).map(t => {
                return this.app.addLoading(this.service.addAttachment(id, t.File)).pipe(tap(() => {
                    t.File = null;
                }), catchError(err => {
                    fileErrors.push({
                        name: t.File.name,
                        error: failedToAdd + ' ' + this.app.getHttpResponseError(err)
                    });
                    return of(0);
                }));
            });

            this.model.Courses.forEach(c => {
                c.Courses.forEach(n => {
                    const outerFiles = n.Attachments.filter(a => !!a.File?.size);

                    outerFiles.forEach(f => {
                        filesToAdd.push(this.app.addLoading(this.service.addAttachment(id, f.File, n.Id)).pipe(tap(() => {
                            f.File = null;
                        }), catchError(err => {
                            fileErrors.push({
                                name: f.File.name,
                                innerCourse: `${c.Code} ${c.Name}`,
                                outerCourse: n.Name,
                                error: failedToAdd + ' ' + this.app.getHttpResponseError(err)
                            });
                            return of(0);
                        })));
                    });
                });
            });

            const afterFileRemoval = () => {
                if (filesToAdd.length) {
                    forkJoin(filesToAdd).subscribe(() => {
                        if (fileErrors.length) {
                            showFileErrors();
                            subj.next(false);
                        } else {
                            subj.next(true);
                        }
                    });
                } else {
                    subj.next(true);
                }
            };

            if (filesToRemove.length) {
                forkJoin(filesToRemove).subscribe(() => {
                    if (fileErrors.length) {
                        showFileErrors();
                    }

                    afterFileRemoval();
                });
            } else {
                afterFileRemoval();
            }
        });

        return subj.asObservable();
    }

    private reload() {
        if (this.isNew) {
            this.app.navigateByUrl('/student-course-equalizations/' + this.model.Id);
        } else {
            this.loadById(this.model.Id);
        }
    }

    private getGradeSortingNumber(grade: IClassifier): number {
        return grade.Code === 'Passed' ? -1 : grade.Code === 'Failed' ? -2 : +grade.Code;
    }

    private hasChanges() {
        const currentData = JSON.stringify(this.model, (key, value) => {
            return value instanceof File ? createFileStub(value.name) : value;
        });

        return currentData != this.originalData;
    }
}
